import React, { FC, useEffect } from "react";
import { useExtractId } from "src/hooks/useExractId";
import { useMediaQuery } from "react-responsive";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  selectorCurrentPost,
  selectorIsLoadingCurrentPost,
} from "src/redux/currentBlog/selector";
import { getBlogCurrentPostAsync } from "src/redux/currentBlog/actions";
import { Description } from "src/page-components/blog/[blog-item]/Description";
import { AboutLabel } from "src/page-components/blog/[blog-item]/AboutLabel";
import { SectionsList } from "src/page-components/blog/[blog-item]/SectionsList";
import { TableOfContent } from "src/page-components/blog/[blog-item]/TableOfContent";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { BreadCrumbs } from "src/components/BreadCrumbs";
import { breadCrumbsTrns } from "src/components/BreadCrumbs/trns";
import { IBreadCrumbsItem } from "src/components/BreadCrumbs/types";
import { Loader } from "src/components/Loader";
import { getImageUrl } from "src/utils/getImageUrl";
import { PATHNAMES } from "src/constants/routes";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";
import { Sizes } from "src/@types/sizes";
import Fancybox from "src/components/Fancybox";
import { MetadataWrapper } from "src/components/MetadataWrapper";

const BlogItem: FC = () => {
  const id = useExtractId();

  const dispatch = useAppDispatch();

  const post = useAppSelector(selectorCurrentPost);
  const isLoading = useAppSelector(selectorIsLoadingCurrentPost);

  useEffect(() => {
    dispatch(getBlogCurrentPostAsync({ id }));
  }, [dispatch, id]);

  const {
    category,
    title,
    meta_title,
    meta_description,
    image,
    short_description,
    description,
    read_time,
    date,
    sections,
  } = post || {};

  const { title: categoryTitle, slug: categorySlug } = category || {};

  const pathBlogWithCategory = `${PATHNAMES.BLOG}/?category=${categorySlug}`;

  const isGLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.GL,
  });

  const breadCrumbsItems: IBreadCrumbsItem[] = [
    { title: { i18nKey: breadCrumbsTrns.home }, path: PATHNAMES.HOME },
    { title: { i18nKey: breadCrumbsTrns.blog }, path: PATHNAMES.BLOG },
    { title: categoryTitle, path: pathBlogWithCategory },
    { title: title },
  ];

  if (!post && !isLoading) return null;

  return (
    <MetadataWrapper title={meta_title} description={meta_description}>
      <PageWrapper mainClassName="py-10 mt-19">
        <section className="container default:overflow-visible">
          {isLoading ? (
            <div className="flex justify-center items-center min-h-72">
              <Loader size={Sizes.XXL} />
            </div>
          ) : (
            <>
              {!isGLScreenBreakpoint && (
                <BreadCrumbs className="mb-10" items={breadCrumbsItems} />
              )}

              <div className="flex gap-10">
                <div className="flex flex-col gap-10 w-full xl:max-w-200">
                  <div className="flex flex-col gap-5">
                    <h1 className="text-purple-base font-semibold">{title}</h1>

                    <Fancybox className="w-full h-60">
                      <a data-fancybox="gallery" href={getImageUrl(image)}>
                        <img
                          className="w-full h-full object-cover object-center rounded-lg"
                          src={getImageUrl(image)}
                          alt={title}
                        />
                      </a>
                    </Fancybox>

                    <Description description={short_description} />

                    <AboutLabel
                      category={categoryTitle}
                      pathBlogWithCategory={pathBlogWithCategory}
                      read_time={read_time}
                      date={date}
                    />

                    <div dangerouslySetInnerHTML={{ __html: description }} />

                    <TableOfContent sections={sections} />
                  </div>

                  <SectionsList sections={sections} />
                </div>

                <TableOfContent sections={sections} isSideBar />
              </div>
            </>
          )}
        </section>
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default BlogItem;
